import React, { useEffect, useRef, useState, memo } from "react";
import { api } from "@/lib/api";

/**
 * Displays a storage-served image by fetching as blob with Bearer auth.
 *
 * Performance optimisations:
 *   - In-Memory-Cache pro Path → kein Re-Fetch beim erneuten Mount
 *     (Mount/Unmount passiert oft bei Auto-Save-Re-Render-Wellen)
 *   - IntersectionObserver-Lazy-Loading → Bilder außerhalb des Viewports
 *     werden gar nicht erst geladen (große Galerien werden flüssig)
 *   - `React.memo` → kein Re-Render wenn `path` und `token` gleich sind
 */

// Globaler Blob-URL-Cache für die Lebensdauer der App-Session.
// Key = path, Value = blob URL. Storage-Pfade ändern sich nie → safe.
const blobCache = new Map();
// Pending Fetches deduplizieren (mehrfaches Mount derselben URL → ein Request)
const pendingFetches = new Map();

async function fetchBlob(path, token) {
  if (blobCache.has(path)) return blobCache.get(path);
  if (pendingFetches.has(path)) return pendingFetches.get(path);

  const promise = api
    .get(`/files/download/${encodeURI(path)}`, {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
    .then((resp) => {
      const url = URL.createObjectURL(resp.data);
      blobCache.set(path, url);
      pendingFetches.delete(path);
      return url;
    })
    .catch((err) => {
      pendingFetches.delete(path);
      throw err;
    });

  pendingFetches.set(path, promise);
  return promise;
}

function AuthImgInner({ path, className, alt, token, onClick, eager = false }) {
  const [src, setSrc] = useState(() => blobCache.get(path) || "");
  const [visible, setVisible] = useState(eager);
  const ref = useRef(null);

  // Lazy: erst laden, wenn das Bild in die Nähe des Viewports kommt
  useEffect(() => {
    if (eager || visible) return;
    if (!ref.current || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            return;
          }
        }
      },
      { rootMargin: "200px" }   // Vorab-Load 200 px vor dem Viewport
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [eager, visible]);

  // Eigentlicher Load (nur wenn `visible` und Cache-Miss)
  useEffect(() => {
    if (!path || !visible) return;
    const cached = blobCache.get(path);
    if (cached) {
      setSrc(cached);
      return;
    }
    let cancelled = false;
    fetchBlob(path, token)
      .then((url) => { if (!cancelled) setSrc(url); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [path, token, visible]);

  if (!src) {
    return (
      <div
        ref={ref}
        className={`${className || ""} bg-zinc-100 animate-pulse`}
        aria-label={alt}
      />
    );
  }
  return <img ref={ref} src={src} alt={alt} className={className} onClick={onClick} loading="lazy" />;
}

const AuthImg = memo(AuthImgInner, (a, b) =>
  a.path === b.path && a.token === b.token && a.className === b.className && a.eager === b.eager
);

export default AuthImg;
